// The Instructions tab: what the agent is told to do — its own text, which it reads at the start of every run, with
// its size — and, open below, what the platform adds to it as one text in the order the model reads it, its parts
// named above it (a click scrolls to one and marks it; scrolling marks the part in view), each part saying where it
// comes from and where it changes.
// A project agent's text is edited here (one commit); a platform agent's is read-only (a project changes it in
// Project override).
import { cn } from '@cortex/ui';
import { Loader2, Pencil } from 'lucide-react';
import * as React from 'react';
import { useSearchParams } from 'react-router';
import { useAgentsHost, useAgentsQuery, useInvalidate, useAgentsKey } from './host.tsx';
import type { AgentTabContext } from './tabs.tsx';
import { btnGhost, btnPrimary, input, messageOf } from './ui.tsx';

interface Part {
  id: string;
  label: string;
  where: React.ReactNode;
  text: string;
}

/** A size people read: characters and about how many tokens (about 4 characters a token). */
export const sizeText = (s: string) =>
  `${s.length.toLocaleString('en-US')} characters · about ${Math.max(1, Math.round(s.length / 4)).toLocaleString('en-US')} tokens`;

const TabLink = ({ tab, children }: { tab: string; children: React.ReactNode }) => {
  const [, setParams] = useSearchParams();
  return (
    <button
      type="button"
      className="border-0 bg-transparent p-0 text-nav-active cursor-pointer hover:underline text-[11.5px]"
      onClick={() =>
        setParams(
          (p) => {
            const n = new URLSearchParams(p);
            n.set('tab', tab);
            return n;
          },
          { replace: true },
        )
      }
    >
      {children}
    </button>
  );
};

const PROJECT_PARTS: Record<string, { label: string; where: React.ReactNode }> = {
  title: { label: 'Title', where: <>from its name: change it with Edit at the top</> },
  rules: { label: 'Rules', where: 'the platform’s, the same for every project agent; not changed here' },
  session: {
    label: 'Session',
    where: 'where it runs, as the host tells it (and the app’s context when an app asks it)',
  },
  how: { label: 'How you work', where: 'the platform’s; not changed here' },
  tools: {
    label: 'Tools',
    where: (
      <>
        from the <TabLink tab="tools">Tools tab ›</TabLink>
      </>
    ),
  },
  guardrails: {
    label: 'Guardrails',
    where: (
      <>
        change them on the <TabLink tab="guardrails">Guardrails tab ›</TabLink>
      </>
    ),
  },
};

const PERSONA_PARTS: Record<string, { label: string; where: string }> = {
  extra: { label: 'This session', where: 'added by the chat for the session; not changed here' },
  common: { label: 'Rules for every persona', where: 'shared by all platform agents; not changed here' },
  rich_charts: { label: 'Rich charts', where: 'only when the person turns Rich charts on in the chat' },
  project_model: {
    label: 'The project',
    where: 'shared by the platform agents that build; not changed here',
  },
  session: { label: 'Session', where: 'where it runs, as the host tells it' },
};

/** What the platform adds, open as one text; the parts above it scroll to one and mark it. */
function Added({ title, parts }: { title: string; parts: Part[] }) {
  const [on, setOn] = React.useState<string | null>(null);
  const box = React.useRef<HTMLDivElement>(null);
  // A click scrolls the text to a part; scrolling marks the part in view ("where am I").
  const scrolling = React.useRef(false);
  const show = (id: string) => {
    setOn(id);
    const el = box.current?.querySelector<HTMLElement>(`[data-part="${id}"]`);
    if (!box.current || !el) return;
    scrolling.current = true;
    box.current.scrollTo?.({ top: el.offsetTop - 6, behavior: 'smooth' });
    window.setTimeout(() => (scrolling.current = false), 600);
  };
  const spy = () => {
    const b = box.current;
    if (!b || scrolling.current) return;
    const els = [...b.querySelectorAll<HTMLElement>('[data-part]')];
    const atEnd = b.scrollTop + b.clientHeight >= b.scrollHeight - 2;
    const here = atEnd ? els[els.length - 1] : els.filter((el) => el.offsetTop <= b.scrollTop + 12).pop();
    setOn(here?.dataset.part ?? els[0]?.dataset.part ?? null);
  };
  if (!parts.length) return null;
  return (
    <section className="rounded-md border border-card-border bg-card-bg p-3" data-testid="instructions-added">
      <div className="text-xs font-semibold text-page-muted">{title}</div>
      <div className="mt-1 mb-2 flex flex-wrap items-center gap-1.5 text-xs text-page-muted">
        Parts:
        {parts.map((p) => (
          <button
            key={p.id}
            type="button"
            data-testid={`instructions-part-${p.id}`}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs cursor-pointer bg-transparent',
              on === p.id ? 'border-nav-active text-nav-active' : 'border-card-border text-page-text',
            )}
            onClick={() => show(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div
        ref={box}
        className="relative max-h-[340px] overflow-auto rounded bg-page-bg px-3 py-2"
        data-testid="instructions-added-text"
        onScroll={spy}
      >
        {parts.map((p) => (
          <div
            key={p.id}
            data-part={p.id}
            className={cn('rounded py-1 transition-colors', on === p.id ? 'bg-nav-active/15' : '')}
          >
            <div className="border-l-2 border-nav-active pl-2 text-[11.5px] text-page-muted">
              {p.label} — {p.where}
            </div>
            <pre className="m-0 mt-1 whitespace-pre-wrap font-mono text-[11.5px] text-page-muted">
              {p.text}
            </pre>
          </div>
        ))}
      </div>
    </section>
  );
}

export function InstructionsTab({ agent }: AgentTabContext) {
  const host = useAgentsHost();
  const k = useAgentsKey();
  const invalidate = useInvalidate();
  const projectAgent = agent.kind === 'project';
  const full = useAgentsQuery(k('agent', agent.key), () => host.client.get(agent.key), {
    enabled: projectAgent,
  });
  const start = useAgentsQuery(k('agent', agent.key, 'run-start'), () => host.client.runStart(agent.key), {
    enabled: projectAgent,
  });
  const [draft, setDraft] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [note, setNote] = React.useState('');

  const text = projectAgent ? (full.data?.persona ?? agent.instructions) : agent.instructions;
  const canEdit = projectAgent && !!full.data?.canManage;
  const save = async () => {
    if (draft === null) return;
    if (!draft.trim()) return setError('The agent needs instructions.');
    setBusy(true);
    setError('');
    try {
      await host.client.save(agent.key, { persona: draft });
      setDraft(null);
      setNote(`Saved as one change. The next run uses it.`);
      invalidate([k()]);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const parts: Part[] = projectAgent
    ? (start.data?.parts ?? []).map((p) => ({
        id: p.id,
        label: PROJECT_PARTS[p.id]?.label ?? p.id,
        where: PROJECT_PARTS[p.id]?.where ?? '',
        text: p.text,
      }))
    : agent.compose
        .filter((s) => s !== 'self')
        .map((s) => ({
          id: s,
          label: PERSONA_PARTS[s]?.label ?? s,
          where: PERSONA_PARTS[s]?.where ?? 'shared by the platform agents; not changed here',
          text:
            s === 'session'
              ? host.session?.length
                ? ['## Session', ...host.session.map((l) => `- ${l}`)].join('\n')
                : '## Session\n(where it runs, as the host tells it when the turn starts)'
              : s === 'extra'
                ? '(the chat’s sections for the session — e.g. what it starts from)'
                : (agent.sections?.[s] ?? ''),
        }));

  return (
    <div className="flex flex-col gap-3" data-testid="instructions-tab">
      {!projectAgent ? (
        <div className="text-xs text-page-muted" data-testid="instructions-readonly">
          Read-only: it ships with the platform.
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-page-muted" data-testid="instructions-size">
          What the agent is told to do — it reads this at the start of every run. {sizeText(draft ?? text)}
        </span>
        {canEdit && draft === null ? (
          <button
            type="button"
            className={btnGhost + ' ml-auto'}
            data-testid="instructions-edit"
            onClick={() => {
              setNote('');
              setDraft(text);
            }}
          >
            <Pencil size={14} /> Edit
          </button>
        ) : null}
      </div>
      {draft !== null ? (
        <>
          <textarea
            className={input + ' min-h-[340px] text-[13px] leading-relaxed'}
            data-testid="instructions-field"
            value={draft}
            maxLength={60_000}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={btnPrimary}
              data-testid="instructions-save"
              disabled={busy || draft === text}
              onClick={() => void save()}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : null} Save
            </button>
            <button
              type="button"
              className={btnGhost}
              data-testid="instructions-cancel"
              disabled={busy}
              onClick={() => {
                setDraft(null);
                setError('');
              }}
            >
              Cancel
            </button>
            <span className="text-xs text-page-muted">
              Saved with the agent as one commit; the next run uses it.
            </span>
          </div>
        </>
      ) : (
        <pre
          data-testid="agent-tab-instructions-text"
          className="m-0 max-h-[420px] overflow-auto whitespace-pre-wrap rounded-md border border-card-border bg-card-bg p-4 font-sans text-[13px] leading-relaxed text-page-text"
        >
          {text || '(none)'}
        </pre>
      )}
      {error ? (
        <div className="text-sm text-red-600" data-testid="instructions-error">
          {error}
        </div>
      ) : null}
      {note ? (
        <div className="text-sm text-page-muted" data-testid="instructions-note">
          {note}
        </div>
      ) : null}
      <Added
        title={
          projectAgent
            ? 'Added by the platform to every run — not edited here.'
            : 'Added by the platform to every run, in this order after its text.'
        }
        parts={parts}
      />
    </div>
  );
}
